/**
 * One decision, N sends (CU-869fhuhy6, UX v3 D22): the Resolution
 * column of a multi-site row is one chip that summarises the sends of
 * the product's rows — the worst status and the count — and the detail
 * lists them one by one.
 *
 * A send is one row of the write log under the decision's `event_id`
 * (plan § 4.4 `sends[]`): `{ pricelist_id, pricelist_code, site,
 * decided_on_site, markup_pct, cap_pct, cap_bound, amount, status,
 * response_code, call_id, origin, sent_at, late, write_kind, decided_net,
 * write_delta, shared }` (the last four since CU-869fkr1a9). `status` is the write
 * log's: `ok | dry_run | unchanged | error | capped | late_not_applied |
 * not_converted | not_sent | outranked | superseded`, and whatever a later
 * engine adds — hence a fallback on every lookup (`patterns.md` § 5).
 *
 * Pure: no React. The chip, its tooltip and the Sends block read it.
 */

//: Worst first. The rank decides the chip's colour and word when a
//: decision has several sends: `refused > capped > not sent > sent`
//: (mock § C), with the engine's other terminal states in between.
//: `not_converted` is last, the order of webback's `SEND_SEVERITY` (CU-869fn0gcp v1.5): it records a
//: site that did not decide in that search, never a send, so it names the chip only when it is all
//: there is — `ok` + `not_converted` is sent.
export const SEND_RANK = [
	'error', 'capped', 'late_not_applied', 'not_sent', 'waiting_tasks', 'pending_resend', 'sibling_sent',
	'superseded', 'unchanged', 'outranked', 'dry_run', 'ok', 'not_converted',
]

//: Chip tone per status (`@components/tones`: green sent, amber limited, red refused, blue waiting,
//: grey nothing happened). `capped`, `not_converted` and `late_not_applied` are new with the
//: products (D22); the rest mirror `SENT_STYLE` of the single-send row so one decision reads the
//: same in both shapes.
export const SEND_STYLE = {
	ok:               { tone: 'ok',      label: 'sent' },
	dry_run:          { tone: 'neutral', label: 'simulated' },
	error:            { tone: 'err',     label: 'refused' },
	capped:           { tone: 'warn',    label: 'capped' },
	not_sent:         { tone: 'info',    label: 'not sent' },
	waiting_tasks:    { tone: 'info',    label: 'not sent · waiting' },
	pending_resend:   { tone: 'info',    label: 're-send pending' },
	sibling_sent:     { tone: 'neutral', label: 'sent through sibling' },
	superseded:       { tone: 'neutral', label: 'superseded' },
	unchanged:        { tone: 'neutral', label: 'already set' },
	outranked:        { tone: 'neutral', label: 'outranked' },
	not_converted:    { tone: 'neutral', label: 'not converted' },
	late_not_applied: { tone: 'neutral', label: 'late · not applied' },
}

//: A status the dictionary does not know still gets a chip — grey, its
//: own code as the word — never a crash and never a green "sent".
const UNKNOWN_STYLE = { tone: 'neutral', label: null }

/** The tone and label of one status, with the fallback for an unknown one. */
export function sendStyle(status) {
	const s = SEND_STYLE[status]
	if (s) return s
	return { ...UNKNOWN_STYLE, label: String(status || 'unknown').replace(/_/g, ' ') }
}

/** True when a send reached the PMS and was taken — counts as "sent". */
export const isSent = (status) => status === 'ok' || status === 'dry_run'

//: An unknown status is treated as worse than everything the dictionary
//: knows: a new word from the engine deserves the operator's eye.
const rankOf = (status) => {
	const i = SEND_RANK.indexOf(status)
	return i === -1 ? -1 : i
}

/** The worst status among the sends, or null for none. */
export function worstStatus(sends) {
	let worst = null
	for (const s of sends || []) {
		if (!s) continue
		if (worst === null || rankOf(s.status) < rankOf(worst)) worst = s.status
	}
	return worst
}

/**
 * `{ sent, capped, refused, not_converted, late_not_applied, total }` — counted here when the server did not send
 * `sends_summary`. `total` leaves `not_converted` out, as the server does (v1.5): a site that did not decide is not a send.
 */
export function summarise(sends) {
	const out = { sent: 0, capped: 0, refused: 0, not_converted: 0, late_not_applied: 0, total: 0 }
	for (const s of sends || []) {
		if (!s) continue
		if (s.status !== 'not_converted') out.total += 1
		if (isSent(s.status)) out.sent += 1
		if (s.status === 'capped') out.capped += 1
		if (s.status === 'error') out.refused += 1
		if (s.status === 'not_converted') out.not_converted += 1
		if (s.status === 'late_not_applied') out.late_not_applied += 1
	}
	return out
}

/**
 * The summary chip of a multi-site row: `{ label, tone, status }`.
 * `sent · 3 of 3` (green) · `capped · 1 of 3` (amber) · `refused · 1 of
 * 3` (red) · `0 of 3 sent` (grey, nothing went out). A cap on one site
 * counts as sent on that site too, so "3 of 3 sent · 1 capped" reads
 * `capped · 1 of 3`: the worst word, the count of that word.
 *
 * v1.5: a site that did not decide (`not_converted`) is not a send — out of the total, said after it
 * (`sent · 1 of 1 · 1 site did not decide`); alone, the chip still says `not converted`.
 */
export function summaryChip(sends, summary = null) {
	const list = sends || []
	const sum = summary || summarise(list)
	const skipped = sum.not_converted ?? list.filter(s => s && s.status === 'not_converted').length
	const total = sum.total ?? list.filter(s => s && s.status !== 'not_converted').length
	const worst = worstStatus(list)
	if (!total) {
		if (!skipped && worst !== 'not_converted') return null
		const style = sendStyle('not_converted')
		return { status: 'not_converted', tone: style.tone, label: style.label }
	}
	const style = sendStyle(worst)
	const tail = skipped ? ` · ${skipped} site${skipped === 1 ? '' : 's'} did not decide` : ''
	if (worst === 'ok' || worst === 'dry_run') {
		return { status: worst, tone: style.tone, label: `${style.label} · ${sum.sent} of ${total}${tail}` }
	}
	const n = list.filter(s => s && s.status === worst).length
	if (sum.sent === 0 && !['capped'].includes(worst)) {
		// Nothing reached the PMS: say so in one breath, then the reason.
		return { status: worst, tone: UNKNOWN_STYLE.tone, label: `0 of ${total} sent${tail}` }
	}
	return { status: worst, tone: style.tone, label: `${style.label} · ${n} of ${total}${tail}` }
}

/**
 * The chip's `title`: one line per site with its status — the operator
 * reads where it went wrong without opening the row. `labelOf` names a
 * site id when the send only carries one.
 */
export function sendsTitle(sends, labelOf = (id) => String(id)) {
	const lines = (sends || []).filter(Boolean).map(s => {
		const site = s.site || (s.converted_for_site != null ? labelOf(s.converted_for_site) : null) || s.pricelist_code || s.pricelist_id || '?'
		const style = sendStyle(s.status)
		const extra = s.status === 'capped' && s.markup_pct != null && s.cap_pct != null
			? ` (markup ${Number(s.markup_pct).toFixed(1)} % > cap ${Number(s.cap_pct).toFixed(0)} %)`
			: s.status === 'error' && s.response_code ? ` (HTTP ${s.response_code})`
			: s.late && isSent(s.status) ? ' (late · priced from the group’s winner)' : ''
		return `${site} · ${style.label}${extra}`
	})
	return lines.join('\n')
}

/** `3 sites · 2 decided · 3 sent`, and `decided on 2 of 3 sites · 1 late` after a flush by deadline (plan § 5.3). */
export function sitesLine(sites, summary, sitesExpected = null) {
	const list = Array.isArray(sites) ? sites : []
	if (!list.length && !summary) return null
	const decided = list.filter(s => s && s.decided).length
	const late = list.filter(s => s && s.late).length
	const parts = []
	if (sitesExpected != null && Number(sitesExpected) > list.length) {
		parts.push(`decided on ${decided} of ${sitesExpected} sites`)
	} else {
		parts.push(`${list.length} site${list.length === 1 ? '' : 's'}`)
		parts.push(`${decided} decided`)
	}
	if (summary && summary.total) {
		if (summary.sent) parts.push(`${summary.sent} sent`)
		if (summary.capped) parts.push(`${summary.capped} capped`)
		if (summary.refused) parts.push(`${summary.refused} refused`)
	}
	if (late) parts.push(`${late} late`)
	return parts.join(' · ')
}

/** The site that won the group — `lowest: true` — or null. */
export function lowestSite(sites) {
	return (Array.isArray(sites) ? sites : []).find(s => s && s.lowest) || null
}

/** `lowest of 2 · DiscoverCars` under the price, or null when one site decided alone. */
export function lowestLine(sites) {
	const list = Array.isArray(sites) ? sites : []
	const decided = list.filter(s => s && s.decided)
	const winner = lowestSite(list)
	if (!winner || decided.length < 2) return null
	return `lowest of ${decided.length} · ${winner.site || winner.site_id}`
}

//: Why a site did not decide, in the words of the engine's codes when
//: the event's reason is one; anything else is already a sentence.
const NO_DECISION = {
	no_competitors_in_scope: 'no competitors in scope',
	no_own_row: 'no own row',
	no_rate_card: 'no own row matched',
	guardrail_no_change: 'guardrail: no change',
	no_price_change: 'no change',
}

/** The short reason under a site that did not decide; a code the screen does not know is said as it came. */
export function noDecisionLabel(reason) {
	if (!reason) return 'no decision'
	return NO_DECISION[reason] || String(reason).replace(/_/g, ' ')
}

const money = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2))
const fmtWriteDelta = (d) => {
	if (!d || d.value == null || d.value === '' || !Number.isFinite(Number(d.value))) return null
	const v = Number(d.value)
	return `${v > 0 ? '+' : ''}${v}${d.kind === 'abs' ? ' €' : ' %'}`
}
const andJoin = (list) => (list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`)

/**
 * The line under a send's pricelist in "Sends · N" (CU-869fkr1a9, criteria
 * 10 and 12), or null when there is nothing to add: `derived +10 %` for a
 * derived; `decided 100.00 · +15 % → 115.00` for a write with a delta (the
 * decided net, the delta, what was sent); and, when rows of the product
 * shared the pricelist, `one send · rows 1 and 3 contributed · row 3 won
 * (lowest value)`. `rowNumberOf(product_row_id)` gives the row's number in
 * the product (the products catalogue), or null — then its site names it.
 */
export function sendDetail(send, rowNumberOf = () => null) {
	if (!send) return null
	const parts = []
	const delta = fmtWriteDelta(send.write_delta)
	if (send.write_kind === 'derived') parts.push(delta ? `derived ${delta}` : 'derived')
	else if (delta) parts.push(`decided ${money(send.decided_net)} · ${delta} → ${money(send.amount)}`)
	const contributors = Array.isArray(send.shared?.contributors) ? send.shared.contributors : []
	if (contributors.length > 1) {
		const known = contributors.map(c => ({ c, n: rowNumberOf(c.product_row_id) }))
		const nameOf = ({ c, n }) => (n != null ? `row ${n}` : (c.site || (c.site_id != null ? `site ${c.site_id}` : 'a row')))
		const ordered = [...known].sort((a, b) => (a.n ?? Infinity) - (b.n ?? Infinity))
		const who = ordered.every(k => k.n != null)
			? `rows ${andJoin(ordered.map(k => String(k.n)))}`
			: andJoin(ordered.map(nameOf))
		const winner = known.find(k => String(k.c.product_row_id) === String(send.shared.winner_row_id))
		parts.push(`one send · ${who} contributed${winner ? ` · ${nameOf(winner)} won (lowest value)` : ''}`)
	}
	return parts.length ? parts.join(' · ') : null
}
